import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/**
  * @module input-helper
  * @description A set of input related utilities such as validation, retrieval
  */
import _ from 'lodash';
import _str from 'underscore.string'; // import path from 'path';

import $ from 'jquery';
import GuiError from './error-helper';
var UNIT_BYTE = 1024 * 1024;
/**
 * Is the given value an integer (or integer like string)?
 * @param {integer|string} n - input
 * @return {boolean}
 *
 * @example
 * isInteger(1) === isInteger('1') === true
 * isInteger(1.2) === isIntger('1.2') === false
 */

export function isInteger(n) {
  return !isNaN(n) && Number(n) % 1 === 0;
}
/**
 * Retrieve all inputs within dom element.<br/>
 * Currently detected elements are:
 * * input[type='text']
 * * input[type='checkbox']
 * * input[type='file']
 * * select
 * * textarea
 *
 * @param {HTMLElement} node - dom element
 * @return {object} Result in key-value pair
 *
 * @example
 * // node = <div><input id='a'/><select name='b'>...</select></div>
 * let data = retrieveFormData(node)
 * // data = { a:'1', b:'2' }
 */

export function retrieveFormData(node) {
  var inputs = $(node).find('input:text, input:checkbox, input:password, input:file, select, textarea');
  var result = {};

  if (inputs.length > 0) {
    result = _.reduce(inputs, function (acc, input) {
      var value = input.value;
      var type = input.type,
          id = input.id,
          name = input.name,
          files = input.files;

      if (type === 'checkbox') {
        value = input.checked;
      } else if (type === 'file') {
        // value = input.files[0];
        var _files = _slicedToArray(files, 1);

        value = _files[0];
      }

      acc[id || name] = value;
      return acc;
    }, {});
  }

  return result;
}
/**
 * Validate field given type/required/validate information
 * @param {number|string} value - value to validate
 * @param {Object} format - format to check value against
 * @param {string} format.name field name
 * @param {'text'|'number'|'integer'|'file'} [format.type='text']
 * @param {boolean} [format.required=false] is this field mandatory?
 * @param {RegExp|string} [format.pattern] validate using regexp pattern
 * @param {string} [format.patternReadable] readable error message for pattern
 * @param {string | Array.<string>} [format.extension] accepted file extension (when type=file)
 * @param {number} [format.min] min value (when type=number|integer)
 * @param {number} [format.max] max value (when type=number|integer|file)
 * @param {boolean|Object} [tOptions=true] - translator options
 * @param {function} [tOptions.et=default error translator] - error translator function
 * @param {function} [tOptions.ft] - field translator function
 * @return {Object<{code:string, params:{field:string,value:(string|object),min:number,max:number,pattern:string,extension:string}}>} returns error object if tOptions=false
 * @return {string} returns translated error message if tOptions is specified
 *
 * @example
 * let error = validateField(7, {name:'field_name',type:'interger',max:6}, false)
 * // error == {code:'out-of-bound', params:{field:'field_name',value:7,max:6}}
 *
 * let error = validateField('07123456', {name:'field_name', pattern:/^[0-9]{10}$/, patternReadable:'not a valid mobile phone #'}, false)
 * // error == {code:'no-match', params:{field:'field_name', value:'07123456', pattern:'not a valid mobile phone #'}}
 *
 * let error = validateField(file, {name: 'file_input', type: 'file', required: true, max: 10}, false)
 * // error == {code: 'file-too-large', params: {field: 'file_input', size: 10, value: File}}
 */

export function validateField(value, _ref) {
  var field = _ref.name,
      _ref$type = _ref.type,
      inputType = _ref$type === void 0 ? 'text' : _ref$type,
      _ref$required = _ref.required,
      required = _ref$required === void 0 ? false : _ref$required,
      pattern = _ref.pattern,
      patternReadable = _ref.patternReadable,
      extension = _ref.extension,
      min = _ref.min,
      max = _ref.max;
  var tOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var errCode = '';
  var errParams = {};

  if (value == null || _str.isBlank(value)) {
    if (required) {
      errCode = 'missing';
    }
  } else if (pattern) {
    if (pattern instanceof RegExp && !pattern.test(value) || _.isString(pattern) && !new RegExp(pattern).test(value)) {
      errCode = 'no-match';
      errParams = {
        pattern: patternReadable,
        message: patternReadable || pattern
      };
    }
  } else if (inputType === 'number' || inputType === 'integer') {
    if (inputType === 'integer' && !isInteger(value)) {
      errCode = 'not-int';
    } else if (inputType === 'number' && isNaN(value)) {
      errCode = 'not-num';
    } else {
      var parsedValue = parseFloat(value);
      var hasMin = min != null;
      var hasMax = max != null;

      if (hasMin && parsedValue < min || hasMax && parsedValue > max) {
        errCode = 'out-of-bound';
        errParams = {
          min: hasMin ? min : '',
          max: hasMax ? max : ''
        };
      }
    }
  } else if (inputType === 'file') {
    // const extName = _.toLower(path.extname(value.name));
    var extName = _.toLower(value.name.split('.').pop());

    var mimeType = value.type;

    if (max && value.size > max * UNIT_BYTE) {
      errCode = 'file-too-large';
      errParams = {
        max: max
      };
    } else if (extension) {
      if (Array.isArray(extension)) {
        var lowerCaseExt = _.map(extension, _.toLower);

        var isPass = _.some(lowerCaseExt, function (el) {
          return el === extName || el === mimeType || RegExp(/^[\w\d]+\/\*$/).test(el) && RegExp(el).test(mimeType);
        });

        if (!isPass) {
          errCode = 'file-wrong-format';
          errParams = {
            extension: lowerCaseExt.toString()
          };
        }
      } else {
        var _lowerCaseExt = _.toLower(extension);

        var isRangedExt = RegExp(/^[\w\d]+\/\*$/).test(_lowerCaseExt);

        var _isPass = extName === _lowerCaseExt || mimeType === _lowerCaseExt || isRangedExt && RegExp(_lowerCaseExt).test(mimeType);

        if (!_isPass) {
          errCode = 'file-wrong-format';
          errParams = {
            extension: _lowerCaseExt
          };
        }
      }
    }
  }

  var error = null;

  if (errCode) {
    error = {
      code: errCode,
      message: _.get(errParams, 'message'),
      params: _objectSpread({
        field: field,
        value: value
      }, _.omit(errParams, 'message'))
    };
  }

  if (tOptions) {
    return new GuiError(error, tOptions).message;
  }

  return error;
}
/**
 * Validate data input(s) against given format.<br/>
 *
 * @param {object} data - key-value pairs
 * @param {object} format - format to check
 * @param {boolean|Object} [tOptions=true] - translator options
 * @param {function} [tOptions.et=default error translator] - error translator function
 * @param {function} [tOptions.ft] - field translator function
 * @return {Array.<error>} Array of errors if tOptions=false. See [validateField]{@link module:input-helper.validateField}
 * @return {string} returns translated error message if tOptions is specified
 *
 * @example
 * let data = {'key1':'value1', 'key2':7, 'key3':3}
 * let format = { required:true, type:'integer', max:6 }
 * let errors = validateData(data, format, false)
 * // errors == [
 * //   {code:'not-int', params:{field:'key1',value:'value1'}},
 * //   {code:'out-of-bound', params:{field:'key2', value:7, max:6}}
 * // ]
 *
 */

export function validateData(data, format) {
  var tOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var result = _.reduce(data, function (acc, v, k) {
    // if all fields use different format, then format[k] is used as validation
    // otherwise assume format is a global format
    var formatToCheck = format[k] || format;

    if (!_.isArray(v)) {
      v = [v];
    }

    if (formatToCheck.required && v.length <= 0) {
      console.warn('validateData::array input required', k);
      acc.push({
        code: 'missing',
        params: {
          field: k
        }
      });
    } else {
      _.forEach(v, function (item) {
        var err = validateField(item, _objectSpread({
          name: k
        }, formatToCheck), false);

        if (err) {
          acc.push(err);
        }
      });
    }

    return acc;
  }, []);

  if (result.length <= 0) {
    result = null;
  }

  if (tOptions) {
    return new GuiError(result, tOptions).message;
  }

  return result;
}
/**
 * Validate form input(s) contained in specified dom node against given format.<br/>
 *
 * @param {HTMLElement} node - dom element containing form inputs
 * @param {object} format - format to check
 * @param {boolean|Object} [tOptions=true] - translator options
 * @param {function} [tOptions.et=default error translator] - error translator function
 * @param {function} [tOptions.ft] - field translator function
 * @return {Array.<error>} Array of errors if tOptions=false. See [validateField]{@link module:input-helper.validateField}
 * @return {string} returns translated error message if tOptions is specified
 *
 */

export function validateForm(node, format, tOptions) {
  var data = retrieveFormData(node);
  return validateData(data, format, tOptions);
}
export default {
  retrieveFormData: retrieveFormData,
  validateField: validateField,
  validateForm: validateForm,
  validateData: validateData
};